import React from 'react';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldSelect,
} from '../../../components';

import css from './ContactUsForm.module.css';

const MESSAGE_MAX_LENGTH = 2000;

const SUBJECT_OPTION_KEYS = [
  'generalInquiry',
  'rentingGear',
  'listingGear',
  'billing',
  'reportAnIssue',
  'other',
];

/**
 * Final Form-based Contact Us form.
 * Renders Name, Email, Subject (dropdown), and Message fields.
 * Submission is delegated to the parent component; this form does not call any API.
 *
 * @param {Object} props
 * @param {function} props.onSubmit - Called with form values on submit.
 * @param {function} [props.onChange] - Called whenever a field value changes.
 * @returns {JSX.Element}
 */
const ContactUsForm = props => {
  const intl = useIntl();

  const nameLabel = intl.formatMessage({ id: 'ContactUsForm.nameLabel' });
  const namePlaceholder = intl.formatMessage({ id: 'ContactUsForm.namePlaceholder' });
  const nameRequiredMessage = intl.formatMessage({ id: 'ContactUsForm.nameRequired' });

  const emailLabel = intl.formatMessage({ id: 'ContactUsForm.emailLabel' });
  const emailPlaceholder = intl.formatMessage({ id: 'ContactUsForm.emailPlaceholder' });
  const emailRequiredMessage = intl.formatMessage({ id: 'ContactUsForm.emailRequired' });
  const emailInvalidMessage = intl.formatMessage({ id: 'ContactUsForm.emailInvalid' });

  const subjectLabel = intl.formatMessage({ id: 'ContactUsForm.subjectLabel' });
  const subjectPlaceholder = intl.formatMessage({ id: 'ContactUsForm.subjectPlaceholder' });

  const messageLabel = intl.formatMessage({ id: 'ContactUsForm.messageLabel' });
  const messagePlaceholder = intl.formatMessage({ id: 'ContactUsForm.messagePlaceholder' });
  const messageRequiredMessage = intl.formatMessage({ id: 'ContactUsForm.messageRequired' });
  const messageTooLongMessage = intl.formatMessage(
    { id: 'ContactUsForm.messageTooLong' },
    { maxLength: MESSAGE_MAX_LENGTH }
  );

  const nameRequired = validators.required(nameRequiredMessage);
  const emailRequired = validators.required(emailRequiredMessage);
  const emailValid = validators.emailFormatValid(emailInvalidMessage);
  const messageRequired = validators.required(messageRequiredMessage);
  const messageMaxLength = validators.maxLength(messageTooLongMessage, MESSAGE_MAX_LENGTH);

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const { handleSubmit, form, pristine, submitting, invalid, onChange } = fieldRenderProps;

        const submitDisabled = invalid || submitting || pristine;

        const onSubmitWithReset = e => {
          const result = handleSubmit(e);
          if (result && typeof result.then === 'function') {
            result.then(() => form.reset());
          } else {
            form.reset();
          }
          return result;
        };

        return (
          <Form
            className={css.root}
            onSubmit={onSubmitWithReset}
            onChange={() => {
              if (onChange) onChange();
            }}
          >
            <FieldTextInput
              className={css.field}
              type="text"
              id="contactUsName"
              name="name"
              autoComplete="name"
              label={nameLabel}
              placeholder={namePlaceholder}
              validate={nameRequired}
            />

            <FieldTextInput
              className={css.field}
              type="email"
              id="contactUsEmail"
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />

            <FieldSelect
              className={css.field}
              id="contactUsSubject"
              name="subject"
              label={subjectLabel}
            >
              <option disabled value="">
                {subjectPlaceholder}
              </option>
              {SUBJECT_OPTION_KEYS.map(key => (
                <option key={key} value={key}>
                  {intl.formatMessage({ id: `ContactUsForm.subjectOption.${key}` })}
                </option>
              ))}
            </FieldSelect>

            <FieldTextInput
              className={css.field}
              type="textarea"
              id="contactUsMessage"
              name="message"
              label={messageLabel}
              placeholder={messagePlaceholder}
              validate={validators.composeValidators(messageRequired, messageMaxLength)}
            />

            <div className={css.submitWrapper}>
              <PrimaryButton type="submit" inProgress={submitting} disabled={submitDisabled}>
                <FormattedMessage id="ContactUsForm.submitLabel" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default ContactUsForm;
